/**
 * Sandbox storage by lifetime — Phase 1 (vitals).
 *
 * The one shape for a box's headroom: the runner samples it every lease
 * heartbeat, the server stores it. Bytes everywhere, so no caller has to guess
 * a unit; `at` is the runner's clock (ms since epoch).
 */
import { z } from 'zod';

const bytes = z.number().int().min(0);
const pair = z.object({ used: bytes, cap: bytes });

/**
 * The runner's in-box file watch (apps/sandbox services/files-watch.ts), 2026-09-30.
 *
 * Its failure was invisible: the runner ships its log lines only from inside a turn, and a
 * watch dies outside one (a staging box ran out of inotify watches and no line ever left it).
 * `live` = every library directory has its watch; `degraded` = some directory's watch
 * failed and the re-scan covers it; `rescan` = past the directory cap, the re-scan covers the
 * rest; `off` = the watch is not running. `dirs` is how many directories are watched.
 */
export const FilesWatchHealthSchema = z.object({
  state: z.enum(['live', 'degraded', 'rescan', 'off']),
  dirs: z.number().int().min(0),
  lastError: z.string().max(200).optional(),
  lastErrorAt: z.number().int().min(0).optional(),
});
export type FilesWatchHealth = z.infer<typeof FilesWatchHealthSchema>;

/** One sample of a box's headroom. Bytes everywhere; `at` is the runner's clock (ms). */
export const SandboxVitalsSchema = z.object({
  at: z.number().int().min(0),
  ram: pair, // statfs('/'): the RAM layer
  vol: pair, // statfs(workspaceRoot): the volume
  mem: z.object({ total: bytes, available: bytes }),
  rss: z.object({ runner: bytes, chrome: bytes }),
  sizes: z
    .object({
      files: bytes,
      apps: bytes,
      tools: bytes,
      work: bytes,
      cache: bytes,
      journal: bytes,
      tmp: bytes,
      // 2026-09-19 — the sheet sums to the disk (apps/sandbox tidy/rollup.ts). Optional so a
      // manifest written by an older runner still parses.
      /** The brain; the user's, like `files`. */
      memory: bytes.optional(),
      /** The legacy working folder; the user's, never swept. */
      scratch: bytes.optional(),
      /** What `df` says is used minus every named root above. Growing = something unclassified. */
      other: bytes.optional(),
      /**
       * 2026-09-26: bytes under the roots whose lifetimes row declares `archiveAfterDays`
       * (`.vestra/legacy`, `lost+found`), taken out of `other`: bytes that leave the box, not
       * data it must keep. It includes any frozen workspace pack in `lost+found`, which the
       * sweep DELETES (tidy/orphans.ts) rather than archives; both are reclaimable, which is
       * what a grow decision asks. Optional so an older runner's beat still parses.
       */
      archivable: bytes.optional(),
    })
    .optional(),
  /**
   * The file watch's health (FilesWatchHealthSchema). Optional so an older runner's beat
   * still parses, and CAUGHT so a value this schema does not know (a newer runner's state)
   * costs only this key, never the sample the headroom rungs act on.
   */
  filesWatch: FilesWatchHealthSchema.optional().catch(undefined),
});
export type SandboxVitals = z.infer<typeof SandboxVitalsSchema>;

export const TidyCountersSchema = z.object({
  deletedBytes: bytes.default(0),
  evictedApps: z.number().int().min(0).default(0),
  /**
   * Derived trees the pressure ladder evicted OUTSIDE `files/apps/<app>` — a `node_modules`
   * in `scratch/`, a browser bundle at the workspace root. Separate from `evictedApps`
   * because the two answer different questions: one says an app lost its dependencies, the
   * other says the box had rebuildable weight nobody had registered as an app at all, which
   * on the prod boxes that filled was 2.6 GB of it.
   *
   * DEFAULTED, not required: a runner that predates this field sends a beat without it, and
   * a required key would cost that box its whole tidy report rather than this one number.
   */
  evictedTrees: z.number().int().min(0).default(0),
  journalPruned: z.number().int().min(0).default(0),
  /**
   * Journals the step brought up to the mirror because the mirror did not hold them whole, and
   * uploads that failed, leaving the file (upload-then-prune, 2026-09-30; a journal it then took
   * is also in `journalPruned`). Optional, so an older runner's beat still parses; absent is zero.
   */
  journalUploaded: z.number().int().min(0).optional(),
  journalUploadFailed: z.number().int().min(0).optional(),
  tmpSwept: z.number().int().min(0).default(0),
  logsPruned: z.number().int().min(0).default(0),
  /**
   * Deletes the box would not make (EACCES on a root-owned profile, EBUSY on an open
   * file). Additive and OPTIONAL so an older runner's beat still parses. It rides the
   * beat because it is the difference between "the sweep found nothing" and "the sweep
   * was refused everything" — two very different boxes that both report zero bytes.
   */
  refused: z.number().int().min(0).optional(),
  /**
   * These counters came from a DRY sweep (Ruling R80): every byte is what the sweeper
   * WOULD have freed, and nothing was deleted. Optional, and absent means enforced —
   * so a reader that has never heard of dry mode reads an enforced sweep, which is
   * what every beat before this field was.
   */
  dry: z.boolean().optional(),
  /**
   * How long the sweep took, in ms — the ladder plus the size roll-up. Optional, because
   * an older runner's beat does not carry it, and summed when several sweeps ride one beat.
   */
  ms: z.number().int().min(0).optional(),
  /**
   * The FIRST step a budget cut short ('orphans' | 'legacy' | 'tmp' | 'cache' | 'work' |
   * 'spills' | 'journal' | 'logs' | 'ladder'). The walk may have continued past it: at boot
   * the journal step has its own slice, so a cut there does not end the sweep. Absent means
   * no step was cut.
   *
   * A free-form string rather than an enum: this is a LABEL the runner writes for a reader,
   * the step list belongs to the sweeper, and a newer runner naming a step this schema has
   * not heard of must not cost the beat its whole tidy report.
   *
   * It rides the beat because `journalPruned: 0` was ambiguous in the way that matters: a
   * box with nothing to prune and a box whose journal step never got to run both reported
   * it, and the second was true of the founder's box, holding 886 expired journals, for
   * weeks (Ruling R144).
   */
  stoppedAt: z.string().max(32).optional(),
  /**
   * Every step that BEGAN, in order (2026-09-28): 'orphans', 'legacy' at boot, then the ring
   * ('tmp' | 'cache' | 'work' | 'spills' | 'journal' | 'logs'), which a turn sweep starts
   * wherever the last one was cut, then 'ladder', always last. `stoppedAt` says where a sweep was cut; this
   * says what it reached, so a starved step shows as absent rather than as a zero. Merged
   * across the sweeps one beat carries, each step once. Labels, like `stoppedAt`, so a newer
   * runner's step name costs nothing; optional, so an older runner's beat still parses.
   */
  ran: z.array(z.string().max(32)).max(16).optional(),
});
export type TidyCounters = z.infer<typeof TidyCountersSchema>;

export const DiskFullSchema = z.object({
  where: z.enum(['ram', 'vol']),
  at: z.number().int().min(0),
});
export type DiskFull = z.infer<typeof DiskFullSchema>;
